import { CURATED_LLMS, CURATED_IMAGE, CURATED_VISION, CURATED_VOICE } from '../data/arenaModels'
import ModelLogo from '../components/models/ModelLogo'
import Wrap from '../components/ui/Wrap'

/* ---- /models — curated "top models" list. A fixed hand-picked set (see
   data/arenaModels.js's CURATED_LLMS/CURATED_IMAGE/CURATED_VISION/
   CURATED_VOICE), not a category-by-category Arena leaderboard — this page
   intentionally has no tabs/filters, just the ranked LLM list plus one
   section per other modality below it.

   Only the LLM table gets ranks and the "Unlimited" In/out+Context columns
   — those are token-generation claims that don't map onto an image, vision,
   or voice model the same way, so the other three sections use a lighter
   row (logo, name, meta, "Runs on LiquidBrain" check) instead of stretching
   the same columns to cover something they don't actually mean there. */

// No `soon` variant — a model still being benchmarked/released doesn't get
// a rank at all (see the "Coming soon" section below, not a row in this
// table), so every row this badge ever renders is already live.
function RankBadge({ children }) {
  return (
    <span className="inline-flex min-w-[34px] items-center justify-center rounded-lg border border-[var(--line)] bg-[var(--paper-2)] px-2.5 py-1 font-mono text-sm font-semibold text-[var(--ink)]">
      {children}
    </span>
  )
}

function ModelMeta({ r }) {
  return (
    <span className="font-mono text-[10px] tracking-[0.03em] text-[var(--faint)]">
      {r.maker}
      {r.params && <> &middot; {r.params} params</>}
      {r.license && <> &middot; <em className="text-[var(--muted)] not-italic">{r.license}</em></>}
    </span>
  )
}

function LBCheck() {
  return (
    <span className="flex justify-center">
      <span className="flex h-7 w-7 items-center justify-center rounded-lg border border-[rgba(62,106,225,.4)] bg-[rgba(62,106,225,.1)] text-[var(--ink-blue)]">
        <svg viewBox="0 0 20 20" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
          <path d="M4 10.5l4 4 8-9" />
        </svg>
      </span>
    </span>
  )
}

function SoonPill() {
  return (
    <span className="flex justify-center">
      <span className="rounded-md border border-[rgba(62,106,225,.4)] px-1.5 py-0.5 font-mono text-[9px] tracking-[0.1em] whitespace-nowrap text-[var(--ink-blue)] uppercase">
        Soon
      </span>
    </span>
  )
}

// A lighter row for the non-ranked sections — no rank, no In/out or Context
// columns (neither concept applies the same way to an image/vision/voice
// model), just identity plus one trailing claim. `soon` swaps the "runs on
// LiquidBrain" check for a "Soon" pill instead — a not-yet-released model
// doesn't get to claim it already runs anywhere.
function TypeSection({ title, blurb, models, soon = false }) {
  return (
    <div className="mt-14">
      <h2 className="text-[19px] font-semibold tracking-[-0.01em]">{title}</h2>
      <p className="mt-1.5 max-w-[620px] text-[14px] leading-[1.55] text-[var(--muted)]">{blurb}</p>
      <div className={`mt-5 overflow-hidden rounded-2xl border ${soon ? 'border-[rgba(62,106,225,.35)]' : 'border-[var(--line)]'}`}>
        {models.map((r) => (
          <div
            key={r.model}
            className={`grid grid-cols-[1fr_50px] items-center gap-2 border-t px-5 py-3.5 first:border-t-0 ${
              soon ? 'border-[rgba(62,106,225,.2)] bg-[rgba(62,106,225,.05)]' : 'border-[var(--line)] hover:bg-[var(--paper-2)]'
            }`}
          >
            <span className="flex min-w-0 items-center gap-2.5">
              <ModelLogo maker={r.maker} />
              <span className="flex min-w-0 flex-col gap-0.5">
                <span className={`text-[14.5px] font-semibold tracking-[-0.01em] ${soon ? 'text-[var(--ink-blue)]' : 'text-[var(--ink)]'}`}>{r.model}</span>
                <ModelMeta r={r} />
              </span>
            </span>
            {soon ? <SoonPill /> : <LBCheck />}
          </div>
        ))}
      </div>
    </div>
  )
}

export default function ModelsPage() {
  const colTemplate = 'grid-cols-[40px_1fr_40px] sm:grid-cols-[64px_1fr_88px_88px_72px]'
  const numColClass = 'hidden sm:block font-mono text-[12.5px] font-semibold text-[var(--ink-blue)]'
  // Ranks only mean something among models that are actually live and
  // benchmarked — a not-yet-released model doesn't get a rank number at
  // all, it gets its own section instead (below).
  const liveLLMs = CURATED_LLMS.filter((m) => !m.soon)
  const soonLLMs = CURATED_LLMS.filter((m) => m.soon)

  return (
    <main className="py-14 md:py-22">
      <Wrap>
        {/* head */}
        <div className="max-w-[740px]">
          <h1 className="text-[clamp(28px,4.4vw,46px)] leading-[1.02] font-bold tracking-[-0.025em]">
            The biggest and best open models. <span className="text-[var(--ink-blue)]">One bill.</span>
          </h1>
          <p className="mt-3.5 max-w-[700px] text-[15.5px] leading-[1.6] text-[var(--muted)]">
            Run privately &amp; securely with one of these <b className="font-semibold text-[var(--ink)]">TOP models</b>.
          </p>
        </div>

        {/* LLM table */}
        <h2 className="mt-11 text-[19px] font-semibold tracking-[-0.01em]">Language models</h2>
        <div className="mt-5 overflow-hidden rounded-2xl border border-[var(--line)]">
          <div className={`grid ${colTemplate} items-center gap-2 bg-[var(--paper-2)] px-5 py-3.5 font-mono text-[9.5px] tracking-[0.1em] text-[var(--faint)] uppercase`}>
            <span>Rank</span>
            <span>Model</span>
            <span className="hidden sm:block">In / out</span>
            <span className="hidden sm:block">Context</span>
            <span className="text-center text-[var(--ink-blue)]">
              <span className="sm:hidden">Runs</span>
              <span className="hidden sm:inline">Runs on LiquidBrain</span>
            </span>
          </div>

          {liveLLMs.map((r, i) => (
            <div key={r.model} className={`grid ${colTemplate} items-center gap-2 border-t border-[var(--line)] px-5 py-3.5 hover:bg-[var(--paper-2)]`}>
              <span>
                <RankBadge>#{i + 1}</RankBadge>
              </span>
              <span className="flex min-w-0 items-center gap-2.5">
                <ModelLogo maker={r.maker} />
                <span className="flex min-w-0 flex-col gap-0.5">
                  <span className="text-[14.5px] font-semibold tracking-[-0.01em] text-[var(--ink)]">{r.model}</span>
                  <ModelMeta r={r} />
                </span>
              </span>
              <span className={numColClass}>Unlimited</span>
              <span className={numColClass}>Unlimited</span>
              <LBCheck />
            </div>
          ))}
        </div>

        {soonLLMs.length > 0 && (
          <TypeSection title="Coming soon" blurb="Not yet live — capacity can still be reserved ahead of release." models={soonLLMs} soon />
        )}

        <TypeSection
          title="Image generation"
          blurb="Open-weight text-to-image, run on the same reserved capacity as your language models."
          models={CURATED_IMAGE}
        />
        <TypeSection
          title="Vision & document understanding"
          blurb="Reads scanned forms, receipts, and reports — not a coding model."
          models={CURATED_VISION}
        />
        <TypeSection
          title="Voice"
          blurb="Speech recognition, audio understanding, and conversation in one place."
          models={CURATED_VOICE}
        />
      </Wrap>
    </main>
  )
}
